import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Logger from '../../../common/logger';
import {
    invalidateInventoryReaders,
    useCombineRoomsMutation,
} from '../../../api/inventory/inventoryApiSlice';
import { readInventoryFailure } from '../../../api/inventory/inventoryErrors';
import type { CombineRoomsResponse } from '../../../api/station/stationApiTypes';
import type { GroupPricingStructure } from '../../../api/group/groupAdminApiSlice';
import CustomButton from '../../../components/CustomButton';
import InputField from '../../../components/ui/InputField';
import CustomModal from '../../common/CustomModal';
import {
    combineRefusalKey,
    selectionProblem,
    suggestCombinedCapacity,
} from '../combineRoomsRules';

interface Props {
    /** The whole room — the room the parts are combined under. */
    whole: GroupPricingStructure;
    /** The rooms that may be offered as parts (`combineCandidates`). */
    candidates: GroupPricingStructure[];
    /** Rooms here that share a Product and so cannot be parts (`sharedProductRooms`). */
    sharedRooms: GroupPricingStructure[];
    /** Guest capacity per product id, for the whole room's suggestion. */
    capacityOf: (productId: string) => number | null;
    /** The whole room's stored capacity, shown so it is never overwritten unseen. */
    wholeCapacity: number | null;
    onClose: () => void;
}

/**
 * "Räume zusammenlegen" — plan `room-houses-and-combining-rooms.md` §4
 * row 4, beside "Raum teilen".
 *
 * Divide CUTS a room into new parts; this DECLARES rooms that already exist as
 * the parts of this one. The parts keep their objects, prices and bookings;
 * the whole room may have no bookings of its own (the server says so if it
 * has). Only rooms in the same house are offered — the server's location rule,
 * applied to the list.
 *
 * Like divide, the dialog stays open after success and says what happened —
 * including how many old bookings of the parts were pointed at their room,
 * because that is a write the operator did not type.
 */
const CombineRoomsDialog = ({ whole, candidates, sharedRooms, capacityOf, wholeCapacity, onClose }: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [selected, setSelected] = useState<string[]>([]);
    const [capacityInput, setCapacityInput] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [result, setResult] = useState<CombineRoomsResponse | null>(null);
    const [combineRooms, { isLoading }] = useCombineRoomsMutation();

    const suggested = useMemo(
        () => suggestCombinedCapacity(selected.map((id) => capacityOf(id))),
        [selected, capacityOf],
    );
    const capacity = capacityInput ?? suggested;
    const problem = selectionProblem(selected);

    const toggle = (productId: string) => {
        setFormError(null);
        setSelected((current) => (current.includes(productId)
            ? current.filter((id) => id !== productId)
            : [...current, productId]));
    };

    const handleSubmit = async () => {
        if (problem || isLoading || !whole.productId) return;
        setFormError(null);
        const typed = capacity.trim();
        try {
            const response = await combineRooms({
                productId: whole.productId,
                partProductIds: selected,
                ...(typed === '' ? {} : { wholeRoomCapacity: Number(typed) }),
            }).unwrap();
            invalidateInventoryReaders(dispatch);
            setResult(response);
        } catch (err) {
            const failure = readInventoryFailure(err);
            const refusalKey = combineRefusalKey(failure.reason);
            if (refusalKey) {
                setFormError(t(refusalKey));
            } else if (failure.unreachable) {
                setFormError(t('services.combine_rooms.unreachable'));
            } else {
                setFormError(failure.message ?? t('services.combine_rooms.save_error'));
            }
            Logger('CombineRoomsDialog').error(`Combine failed: ${JSON.stringify(err)}`);
        }
    };

    if (result) {
        return (
            <CustomModal
                open={true}
                title={t('services.combine_rooms.done_title')}
                description={t('services.combine_rooms.done_description', {
                    room: whole.name ?? '',
                    parts: result.parts.length,
                })}
                onClose={onClose}
                saveButton={<CustomButton title={t('common.close')} onClick={onClose} />}
            >
                {result.overlappingBookings > 0 && (
                    <p className={'text-sm text-error'}>
                        {t('services.combine_rooms.overlap_notice', { count: result.overlappingBookings })}
                    </p>
                )}
                {result.pinnedBookings > 0 && (
                    <p className={'text-sm text-foreground/70'}>
                        {t('services.combine_rooms.pinned_notice', { count: result.pinnedBookings })}
                    </p>
                )}
            </CustomModal>
        );
    }

    return (
        <CustomModal
            open={true}
            title={t('services.combine_rooms.title', { room: whole.name ?? '' })}
            description={t('services.combine_rooms.description')}
            onClose={onClose}
            saveButton={
                <CustomButton
                    title={t('services.combine_rooms.submit')}
                    onClick={handleSubmit}
                    disabled={problem !== null || isLoading}
                    loading={isLoading}
                />
            }
            closeButton={<CustomButton title={t('common.cancel')} isFilled={false} onClick={onClose} />}
        >
            <div className={'flex flex-col gap-4'}>
                <p className={'text-sm text-foreground/70'}>{t('services.combine_rooms.booking_warning')}</p>
                {candidates.length === 0 ? (
                    <p className={'text-sm text-foreground/60'}>{t('services.combine_rooms.no_candidates')}</p>
                ) : (
                    <ul className={'flex flex-col gap-2'}>
                        {candidates.map((candidate) => (
                            <li key={candidate.productId}>
                                <label className={'flex items-center gap-2 text-sm text-foreground'}>
                                    <input
                                        type={'checkbox'}
                                        className={'accent-primary'}
                                        checked={selected.includes(candidate.productId ?? '')}
                                        onChange={() => toggle(candidate.productId ?? '')}
                                    />
                                    {candidate.name ?? candidate.productId}
                                </label>
                            </li>
                        ))}
                    </ul>
                )}
                {sharedRooms.length > 0 && (
                    <p className={'text-xs text-foreground/60'}>
                        {t('services.combine_rooms.shared_product_hint', {
                            rooms: sharedRooms.map((room) => room.name ?? room.productId).join(', '),
                        })}
                    </p>
                )}
                <InputField
                    label={t('services.combine_rooms.capacity_label')}
                    value={capacity}
                    onChange={(value) => setCapacityInput(value)}
                    type={'number'}
                    min={1}
                />
                <p className={'text-xs text-foreground/60'}>
                    {t('services.combine_rooms.capacity_hint')}
                    {wholeCapacity !== null && ` ${t('services.combine_rooms.capacity_stored', { guests: wholeCapacity })}`}
                </p>
                {problem && selected.length > 0 && (
                    <p className={'text-sm text-foreground/60'}>{t(problem)}</p>
                )}
                {formError && <p className={'text-sm text-error'}>{formError}</p>}
            </div>
        </CustomModal>
    );
};

export default CombineRoomsDialog;
